import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Store } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { fmtApiError } from "@/lib/api";
import { Logo, LogoMark } from "@/components/Logo";

const CUENTAS_DEMO = [
  { usuario: "local-centro", etiqueta: "Kiosco — Local Centro" },
  { usuario: "local-norte", etiqueta: "Kiosco — Local Norte" },
  { usuario: "local-sur", etiqueta: "Kiosco — Local Sur" },
  { usuario: "rrhh", etiqueta: "RRHH — Asistencia" },
  { usuario: "liquidacion", etiqueta: "Liquidación — Contadora" },
];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [usuario, setUsuario] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [cargando, setCargando] = useState(false);

  const entrar = async (e) => {
    e.preventDefault();
    setError("");
    setCargando(true);
    try {
      const u = await login(usuario, password);
      navigate(u.rol === "kiosco" ? "/kiosco" : u.rol === "rrhh" ? "/rrhh/hoy" : "/liquidacion/resumen", { replace: true });
    } catch (err) {
      setError(fmtApiError(err));
    } finally {
      setCargando(false);
    }
  };

  return (
    <div className="grid min-h-screen bg-background lg:grid-cols-[1.15fr_1fr]" data-testid="pantalla-login">
      <div className="relative hidden flex-col justify-between overflow-hidden border-r border-border p-12 lg:flex">
        <Logo size={40} sub="Sistema de fichaje de personal" />
        <div>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="max-w-lg text-5xl font-bold leading-[1.08] tracking-tight text-foreground"
          >
            El fichaje es la <span className="text-primary">fuente de verdad</span>.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.22 }}
            className="mt-5 max-w-md text-base leading-relaxed text-muted-foreground"
          >
            La empleada ficha en el kiosco del local, RRHH lo ve al instante y la liquidación del mes sale sola.
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.35 }}
            className="mt-10 flex gap-3"
          >
            {["Centro", "Norte", "Sur"].map((l) => (
              <span key={l} className="flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-xs font-medium text-muted-foreground">
                <Store size={13} className="text-primary" /> Local {l}
              </span>
            ))}
          </motion.div>
        </div>
        <div className="pointer-events-none absolute -bottom-24 -right-24 opacity-[0.05]">
          <LogoMark size={420} />
        </div>
        <p className="text-xs text-muted-foreground/60">Demo online — empresa y datos ficticios</p>
      </div>

      <div className="flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-sm"
        >
          <div className="mb-8 lg:hidden">
            <Logo size={36} sub="Sistema de fichaje" />
          </div>
          <h2 className="text-2xl font-bold tracking-tight text-foreground">Ingresar</h2>
          <p className="mt-1 text-sm text-muted-foreground">Cada perfil ve solo sus pantallas.</p>

          <form onSubmit={entrar} className="mt-7 space-y-4" data-testid="form-login">
            <div>
              <label htmlFor="usuario" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Usuario
              </label>
              <input
                id="usuario"
                data-testid="input-usuario"
                value={usuario}
                onChange={(e) => setUsuario(e.target.value)}
                autoComplete="username"
                placeholder="ej. rrhh"
                className="w-full rounded-xl border border-input bg-card px-4 py-3 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground/50 focus:border-primary"
              />
            </div>
            <div>
              <label htmlFor="password" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Contraseña
              </label>
              <input
                id="password"
                type="password"
                data-testid="input-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                placeholder="••••••••"
                className="w-full rounded-xl border border-input bg-card px-4 py-3 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground/50 focus:border-primary"
              />
            </div>
            {error && (
              <p className="rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-2.5 text-sm text-destructive" data-testid="error-login">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={cargando}
              data-testid="boton-ingresar"
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-bold text-primary-foreground transition-all hover:brightness-110 active:scale-[0.98] disabled:opacity-60"
            >
              {cargando ? "Ingresando…" : "Ingresar"} <ArrowRight size={16} />
            </button>
          </form>

          <div className="mt-8 rounded-2xl border border-border bg-card/60 p-4" data-testid="cuentas-demo">
            <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Cuentas demo — contraseña <span className="font-mono-num text-primary">demo1234</span>
            </p>
            <div className="space-y-1">
              {CUENTAS_DEMO.map((c) => (
                <button
                  key={c.usuario}
                  type="button"
                  onClick={() => { setUsuario(c.usuario); setPassword("demo1234"); toast.info(`Cuenta ${c.usuario} lista para ingresar`); }}
                  className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-xs text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                  data-testid={`cuenta-${c.usuario}`}
                >
                  <span>{c.etiqueta}</span>
                  <span className="font-mono-num text-[11px] text-primary/80">{c.usuario}</span>
                </button>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
